import {
  extractUniqueHeadersLiveboards,
  liveboardAndDataObjects,
} from "./extractHeadersLiveboards";
import { extractUniqueHeadersData } from "./extractHeadersData";
import { useLazyGetTSAccessTokenQuery } from "../../services/serverless";
import {
  useLazyListLiveboardsQuery,
  useLazyListTablesQuery,
} from "../../services/thoughtSpotV1";
import { useGetMetadataDetailsMutation } from "../../services/thoughtSpotV2";
import { saveCSV } from "../ExportToCSV";
import { LiveboardInfo } from "../../services/types/thoughtSpot";
import { metadataToCsv } from "./helperFunctions";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Error } from "../../components/Error";

interface TSMetadataToolProps {
  orgID: number;
  url: string;
  filenamePostfix: string;
}

export const TSMetadataTool = (props: TSMetadataToolProps) => {
  const [tsAccessTokenQuery, tsAccessTokenResult] =
    useLazyGetTSAccessTokenQuery();
  const [listLiveboardsQuery, listLiveboardsResults] =
    useLazyListLiveboardsQuery();
  const [listTablesQuery, listTablesResults] = useLazyListTablesQuery();
  const [getMetadataDetailsQuery, getMetadataDetailsResult] =
    useGetMetadataDetailsMutation();
  const [tsToken, setTSToken] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [lifecycle, setLifecycle] = useState<string>("Idle");
  const [errorFlag, setErrorFlag] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<string>("");
  const [errorType, setErrorType] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    tsAccessTokenQuery(props.orgID);
  }, [props]);

  useEffect(() => {
    if (
      tsAccessTokenResult.isSuccess &&
      tsAccessTokenResult.status === "fulfilled"
    ) {
      setTSToken(tsAccessTokenResult.data.token);
      setLoading(false);
    }
  }, [tsAccessTokenResult]);

  async function getLiveboardInfo(): Promise<void> {
    if (tsToken !== undefined) {
      const newTokenQuery = await tsAccessTokenQuery(props.orgID).unwrap();
      setTSToken(newTokenQuery.token);
      setLifecycle(`Downloading Liveboard List`);
      const liveboardList = await listLiveboardsQuery({
        thoughtSpotAccessToken: tsToken,
      }).unwrap();

      let liveboardDataResults: liveboardAndDataObjects[] = [];

      const trimmedLiveboardList: LiveboardInfo[] = [];

      liveboardList.forEach((value) => {
        if (value.orgIds && !value.orgIds.includes(0)) {
          trimmedLiveboardList.push(value);
        }
      });

      for (let i = 0; i < trimmedLiveboardList.length; i += 10) {
        try {
          setLifecycle(
            `Downloading data for liveboards ${i + 1} to ${i + 11} / ${trimmedLiveboardList.length}, please don't leave this page.`,
          );
          const tempMetadata = await getMetadataDetailsQuery({
            thoughtSpotAccessToken: tsToken,
            metadataType: "LIVEBOARD",
            metadataIdArray: trimmedLiveboardList
              .slice(i, i + 10)
              .map((value) => {
                return value.id;
              }),
            responseVersion: "V2",
          }).unwrap();
          const metadataProcessing =
            await extractUniqueHeadersLiveboards(tempMetadata);
          liveboardDataResults = [
            ...liveboardDataResults,
            ...metadataProcessing,
          ];
        } catch {
          try {
            const newToken = await tsAccessTokenQuery(props.orgID).unwrap();
            setTSToken(newToken.token);
            const tempMetadata = await getMetadataDetailsQuery({
              thoughtSpotAccessToken: tsToken,
              metadataType: "LIVEBOARD",
              metadataIdArray: trimmedLiveboardList
                .slice(i, i + 10)
                .map((value) => {
                  return value.id;
                }),
              responseVersion: "V2",
            }).unwrap();
            const metadataProcessing =
              await extractUniqueHeadersLiveboards(tempMetadata);
            liveboardDataResults = [
              ...liveboardDataResults,
              ...metadataProcessing,
            ];
          } catch {
            setErrorFlag(true);
            setErrorCode("403");
            setErrorMessage(
              "Invalid TS Token. Please refresh the page and try again.",
            );
            setErrorType("ExternalAPI");
          }
        }
      }

      saveCSV(
        `Liveboard-Metadata-${props.filenamePostfix}.csv`,
        metadataToCsv(liveboardDataResults, props.url, false, true),
      );
      setLifecycle("Idle");
    }
  }

  async function getDataInfo(): Promise<void> {
    if (tsToken !== undefined) {
      const newTokenQuery = await tsAccessTokenQuery(props.orgID).unwrap();
      setTSToken(newTokenQuery.token);
      setLifecycle(`Downloading Data Model List`);
      const dataModelList = await listTablesQuery({
        thoughtSpotAccessToken: newTokenQuery.token,
      }).unwrap();

      const trimmedDataModelList: any[] = [];

      dataModelList.forEach((value) => {
        if (value.orgIds && !value.orgIds.includes(0)) {
          trimmedDataModelList.push(value);
        }
      });

      let dataModelResults: liveboardAndDataObjects[] = [];

      for (let i = 0; i < trimmedDataModelList.length; i += 10) {
        try {
          setLifecycle("Downloading data, please wait.");
          const tempMetadata = await getMetadataDetailsQuery({
            thoughtSpotAccessToken: newTokenQuery.token,
            metadataType: "LOGICAL_TABLE",
            metadataIdArray: trimmedDataModelList
              .slice(i, i + 10)
              .map((value) => {
                return value.id;
              }),
            responseVersion: "V1",
          }).unwrap();
          const metadataProcessing =
            await extractUniqueHeadersData(tempMetadata);
          dataModelResults = [...dataModelResults, ...metadataProcessing];
        } catch {
          try {
            const newToken = await tsAccessTokenQuery(props.orgID).unwrap();
            setTSToken(newToken.token);
            const tempMetadata = await getMetadataDetailsQuery({
              thoughtSpotAccessToken: newToken.token,
              metadataType: "LOGICAL_TABLE",
              metadataIdArray: trimmedDataModelList
                .slice(i, i + 10)
                .map((value) => {
                  return value.id;
                }),
              responseVersion: "V1",
            }).unwrap();
            const metadataProcessing =
              await extractUniqueHeadersData(tempMetadata);
            dataModelResults = [...dataModelResults, ...metadataProcessing];
          } catch {
            setErrorFlag(true);
            setErrorCode("403");
            setErrorMessage(
              "Invalid TS Token. Please refresh the page and try again.",
            );
            setErrorType("ExternalAPI");
          }
        }
      }
      saveCSV(
        `Data-Models-Metadata-${props.filenamePostfix}.csv`,
        metadataToCsv(dataModelResults, props.url, false, false),
      );
      setLifecycle("Idle");
    }
  }

  return (
    <div className="container roundedCorners">
      <div className="mb-2">
        <div
          className="col-12 module-title"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          Metadata Download Tool
        </div>
      </div>
      {(tsToken === undefined || loading) && (
        <div className="loading-div">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            spin={true}
            style={{ margin: "0 auto" }}
          />
        </div>
      )}
      {tsToken !== undefined && !loading && (
        <div>
          <div style={{ display: "flex" }}>
            <button
              className="me-2 d-flex flex-row align-items-center px-2 py-1 btn btn-primary"
              disabled={
                tsToken === undefined || loading || lifecycle !== "Idle"
              }
              onClick={getLiveboardInfo}
            >
              Download liveboard metadata
            </button>
            <button
              className="me-2 d-flex flex-row align-items-center px-2 py-1 btn btn-primary"
              disabled={
                tsToken === undefined || loading || lifecycle !== "Idle"
              }
              onClick={getDataInfo}
            >
              Download data model metadata
            </button>
          </div>
          <div className="lifecycle-text">{lifecycle}</div>
        </div>
      )}
      {errorFlag && (
        <div>
          <Error code={errorCode} type={errorType} message={errorMessage} />
        </div>
      )}
    </div>
  );
};
